import { useMemo, useState } from 'react';
import SalesLayout from '../../components/SalesLayout';
import { PageHero, HeroButton, HeroIcons } from '../../components/SalesUI';

const DEFAULT_PROGRAM_NAME = 'BEng (Hons) in Software Engineering (TOP UP) of London Metropolitan University';

const ensureTitleSuffix = (value) => {
    if (!value) {
        return '';
    }

    return value.endsWith('.') ? value : `${value}.`;
};

const formatDateLong = (value) => {
    if (!value) {
        return '';
    }

    const [yearString, monthString, dayString] = value.split('-');
    const year = Number(yearString);
    const month = Number(monthString);
    const day = Number(dayString);

    if (!year || !month || !day) {
        return value;
    }

    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

const splitAddressLines = (address) => {
    return address
        .split(/\r?\n/) // Preserve manual line breaks
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
};

const escapeHtml = (value) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const deriveLetterContent = (data) => {
    const letterDate = formatDateLong(data.offerDate) || data.offerDate;
    const deadline = formatDateLong(data.submissionDeadline) || data.submissionDeadline;
    const titleWithSuffix = ensureTitleSuffix(data.title);
    const recipientName = `${[titleWithSuffix, data.studentFullName].filter(Boolean).join(' ')}`.replace(/\s+/g, ' ').trim();
    const addressLines = splitAddressLines(data.studentAddress);
    const subjectLine = `Conditional Offer Letter for ${DEFAULT_PROGRAM_NAME} ${data.intake}`;

    return {
        letterDate,
        deadline,
        recipientName,
        addressLines,
        subjectLine,
    };
};

const buildLetterHtml = (data) => {
    const { letterDate, deadline, recipientName, addressLines, subjectLine } = deriveLetterContent(data);

    const addressHtml = addressLines
        .map((line) => `<p style="margin:4px 0;">${escapeHtml(line)}</p>`)
        .join('');

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <title>Offer Letter Preview</title>
    <style>
        @page { size: A4; margin: 0; }
        :root { color-scheme: light; }
        body { margin: 0; background: #f8fafc; font-family: 'Times New Roman', serif; color: #1f2937; line-height: 1.55; font-size: 12px; }
        .page-wrapper { max-width: 210mm; margin: 0 auto; padding: 24px; }
        .letter-container { background: #ffffff; border-radius: 12px; padding: 48px; box-shadow: 0 20px 45px rgba(15, 23, 42, 0.1); }
        .meta { text-align: right; font-weight: 500; color: #475569; }
        .recipient p { margin: 4px 0; }
        .subject { font-weight: 600; text-decoration: underline; color: #1e293b; }
        .body-text p { margin: 0 0 16px 0; text-align: justify; }
        .body-text ul { margin: 16px 0; padding-left: 20px; }
        .signature-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 32px; margin-top: 48px; }
        .signature-grid .line { height: 24px; border-bottom: 1px dashed #94a3b8; }
        .print-actions { display: flex; justify-content: flex-end; gap: 12px; margin-bottom: 16px; }
        .print-button { background: #1d4ed8; color: #ffffff; border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-family: 'Segoe UI', sans-serif; cursor: pointer; }
        .print-button:hover { background: #1e40af; }
        @media print {
            body { background: #ffffff; }
            .page-wrapper { max-width: none; padding: 25mm; }
            .letter-container { box-shadow: none; border-radius: 0; border: 0; padding: 0; background: transparent; }
            .print-actions { display: none; }
        }
    </style>
</head>
<body>
    <div class="page-wrapper">
        <div class="print-actions">
            <button class="print-button" onclick="window.print()">Print</button>
        </div>
        <div class="letter-container">
            <div class="meta">${escapeHtml(letterDate)}</div>
            <div class="recipient" style="margin-top: 24px;">
                <p style="font-weight:600;">${escapeHtml(recipientName)}</p>
                ${addressHtml}
            </div>
            <div style="margin-top: 24px;">
                <p class="subject">${escapeHtml(subjectLine)}</p>
                <p style="font-weight:500;color:#475569;">${escapeHtml(data.intake)}</p>
            </div>
            <div class="body-text" style="margin-top: 24px;">
                <p>Dear ${escapeHtml(recipientName)},</p>
                <p><strong>Congratulations!</strong> We are pleased to inform you that your application for registration has been accepted by ESOFT subject to the completion of the requirement below to enrol for the ${escapeHtml(DEFAULT_PROGRAM_NAME)} degree offered at ESOFT Metro Campus in collaboration with London Metropolitan University (LMU).</p>
                <p style="font-weight:600;color:#1e293b;">The conditional requirement for completing the registration</p>
                <ul>
                    <li>Submission of transcript/certificate of the Pearson BTEC HND in Computing on or before <strong>${escapeHtml(deadline)}</strong></li>
                </ul>
                <p>If you fail to complete the above conditional requirement on or before the deadline, you will be transferred to the next intake until you complete the stated conditions.</p>
                <p>This programme is a 12 months Top-up degree designed for the working professional. The quality of Kingston University (KU) is assured by QAA UK and is a member organization of the Association of Commonwealth Universities and internationally recognized. Also, LMU is a recognized university by the University Grants Commission of Sri Lanka.</p>
                <p>The partnership between ESOFT Metro Campus and London Metropolitan University was established in 2012 with the launching of the Top-up degree programme. Many successful undergraduates have paved a name for themselves as corporate leaders and entrepreneurs in the industry after completion of their graduation, you too will be an alumnus of a reputed and recognized global institution.</p>
                <p>We have commenced the registration of new students for the ${escapeHtml(data.intake)}.</p>
                <p>I would like to warmly welcome you to ESOFT Metro Campus and the London Metropolitan University.</p>
                <p>Good luck.</p>
                <p>Thank you.</p>
                <p>Kindly put your name and signature to this as &ldquo;Received and Accepted the Offer&rdquo; to proceed.</p>
            </div>
            <div class="signature-grid">
                <div>
                    <p style="font-weight:600;">Student Name:</p>
                    <div class="line"></div>
                </div>
                <div>
                    <p style="font-weight:600;">Student Signature:</p>
                    <div class="line"></div>
                </div>
            </div>
        </div>
    </div>
    <script>
        window.addEventListener('load', function () { window.focus(); });
    </script>
</body>
</html>`;
};

export default function OfferLetter({ pageTitle, section }) {
    const titleOptions = useMemo(() => ['Mr', 'Ms', 'Mrs', 'Miss', 'Dr', 'Prof'], []);
    const intakeOptions = useMemo(() => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const nextYear = currentYear + 1;
        const months = [1, 8]; // February, September (zero-based months)

        const buildOption = (year, monthIndex) => {
            const monthLabel = new Date(year, monthIndex, 1).toLocaleString('en-US', { month: 'long' });
            const label = `${monthLabel} ${year} Intake`;
            return {
                label,
                value: label,
                year,
                monthIndex,
                timestamp: new Date(year, monthIndex, 1).getTime(),
            };
        };

        const options = [];
        [currentYear, nextYear].forEach((year) => {
            months.forEach((monthIndex) => {
                options.push(buildOption(year, monthIndex));
            });
        });

        return options;
    }, []);

    const defaultIntake = useMemo(() => {
        if (intakeOptions.length === 0) {
            return '';
        }

        const now = new Date();
        const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        const upcoming = intakeOptions.find((option) => option.timestamp >= currentMonthStart);
        return (upcoming ?? intakeOptions[0]).value;
    }, [intakeOptions]);

    const formatDateForInput = (value) => {
        const offsetMs = value.getTimezoneOffset() * 60_000;
        const normalized = new Date(value.getTime() - offsetMs);
        return normalized.toISOString().split('T')[0];
    };

    const [formData, setFormData] = useState(() => ({
        title: titleOptions[0],
        studentFullName: '',
        studentAddress: '',
        intake: defaultIntake,
        offerDate: formatDateForInput(new Date()),
        submissionDeadline: '',
    }));

    const [formErrors, setFormErrors] = useState({});
    const [previewData, setPreviewData] = useState(null);
    const previewContent = useMemo(() => (previewData ? deriveLetterContent(previewData) : null), [previewData]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const validateForm = (data) => {
        const nextErrors = {};

        if (!data.studentFullName.trim()) {
            nextErrors.studentFullName = 'Student full name is required.';
        }

        if (!data.studentAddress.trim()) {
            nextErrors.studentAddress = 'Student address is required.';
        }

        if (!data.intake.trim()) {
            nextErrors.intake = 'Select an intake option.';
        }

        if (!data.offerDate) {
            nextErrors.offerDate = 'Select the offer letter date.';
        }

        if (!data.submissionDeadline) {
            nextErrors.submissionDeadline = 'Provide the assignment submission deadline.';
        }

        return nextErrors;
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const nextErrors = validateForm(formData);
        setFormErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            setPreviewData(null);
            return;
        }

        const submittedData = { ...formData };
        setPreviewData(submittedData);

        if (typeof window !== 'undefined') {
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                const letterHtml = buildLetterHtml(submittedData);
                printWindow.document.open();
                printWindow.document.write(letterHtml);
                printWindow.document.close();
                printWindow.focus();
            } else {
                console.warn('Unable to open the offer letter preview window. Please allow pop-ups and try again.');
            }
        }
    };

    return (
        <SalesLayout title={pageTitle} currentSection={section}>
            <div className="sales-stagger space-y-6">
                <PageHero
                    icon={HeroIcons.offer}
                    title={pageTitle || 'Offer letter'}
                    subtitle="Fill in the prospect's details and produce a polished, ready-to-send offer letter in seconds."
                    gradient="bg-gradient-to-br from-fuchsia-600 via-purple-600 to-indigo-600"
                />
                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-6">
                        <div>
                            <h2 className="text-base font-semibold text-slate-900">Generate Offer Letter</h2>
                            <p className="mt-1 text-sm text-slate-500">Capture the prospect details before producing the official letter.</p>
                        </div>

                        <form className="space-y-5" onSubmit={handleSubmit} noValidate>
                            <div className="grid gap-5">
                                <div className="grid gap-2">
                                    <label className="text-sm font-medium text-slate-700" htmlFor="title">
                                        Title <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        id="title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        {titleOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid gap-2">
                                    <label className="text-sm font-medium text-slate-700" htmlFor="studentFullName">
                                        Student Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="studentFullName"
                                        name="studentFullName"
                                        value={formData.studentFullName}
                                        onChange={handleInputChange}
                                        placeholder="Enter student's full name"
                                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                    {formErrors.studentFullName && (
                                        <p className="text-sm text-red-600">{formErrors.studentFullName}</p>
                                    )}
                                </div>

                                <div className="grid gap-2">
                                    <label className="text-sm font-medium text-slate-700" htmlFor="studentAddress">
                                        Student Address <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        id="studentAddress"
                                        name="studentAddress"
                                        value={formData.studentAddress}
                                        onChange={handleInputChange}
                                        placeholder="Enter student's address"
                                        rows={3}
                                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                    {formErrors.studentAddress && (
                                        <p className="text-sm text-red-600">{formErrors.studentAddress}</p>
                                    )}
                                </div>

                                <div className="grid gap-2">
                                    <label className="text-sm font-medium text-slate-700" htmlFor="intake">
                                        Intake <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        id="intake"
                                        name="intake"
                                        value={formData.intake}
                                        onChange={handleInputChange}
                                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        {intakeOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    {formErrors.intake && <p className="text-sm text-red-600">{formErrors.intake}</p>}
                                </div>

                                <div className="grid gap-2">
                                    <label className="text-sm font-medium text-slate-700" htmlFor="offerDate">
                                        Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="offerDate"
                                        name="offerDate"
                                        type="date"
                                        value={formData.offerDate}
                                        onChange={handleInputChange}
                                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                    {formErrors.offerDate && <p className="text-sm text-red-600">{formErrors.offerDate}</p>}
                                </div>

                                <div className="grid gap-2">
                                    <label className="text-sm font-medium text-slate-700" htmlFor="submissionDeadline">
                                        Assignment Submission Deadline <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="submissionDeadline"
                                        name="submissionDeadline"
                                        type="date"
                                        value={formData.submissionDeadline}
                                        onChange={handleInputChange}
                                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                    {formErrors.submissionDeadline && (
                                        <p className="text-sm text-red-600">{formErrors.submissionDeadline}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    Generate Letter
                                </button>
                            </div>
                        </form>

                        {previewData && previewContent && (
                            <div className="mx-auto w-full max-w-[210mm] rounded-lg bg-white px-10 pb-10 pt-10 text-slate-800 shadow-sm print:max-w-none print:bg-white print:p-0 print:shadow-none">
                                    <div className="space-y-6 font-serif text-[12px] leading-[1.55]">
                                    <div className="flex justify-end text-[13px] font-medium text-slate-700">
                                        {previewContent.letterDate}
                                    </div>

                                    <div className="space-y-1 text-slate-700">
                                        <p className="font-medium">{previewContent.recipientName}</p>
                                        {previewContent.addressLines.map((line, index) => (
                                            <p key={`${line}-${index}`}>{line}</p>
                                        ))}
                                    </div>

                                    <div className="space-y-2">
                                        <h3 className="text-[13px] font-semibold text-slate-900 underline">{previewContent.subjectLine}</h3>
                                        <p className="font-medium text-slate-700">{previewData.intake}</p>
                                    </div>

                                    <div className="space-y-4 text-slate-700">
                                        <p>Dear {previewContent.recipientName},</p>

                                        <p className="text-justify">
                                            <span className="font-semibold">Congratulations!</span> We are pleased to inform you that your application for registration has been accepted by ESOFT subject to the completion of the requirement below to enrol for the {DEFAULT_PROGRAM_NAME} degree offered at ESOFT Metro Campus in collaboration with London Metropolitan University (LMU).
                                        </p>

                                        <div>
                                            <p className="font-semibold text-slate-900">The conditional requirement for completing the registration</p>
                                            <ul className="mt-2 list-disc space-y-2 pl-6">
                                                <li>
                                                    Submission of transcript/certificate of the Pearson BTEC HND in Computing on or before <span className="font-semibold text-slate-900">{previewContent.deadline}</span>
                                                </li>
                                            </ul>
                                        </div>

                                        <p className="text-justify">
                                            If you fail to complete the above conditional requirement on or before the deadline, you will be transferred to the next intake until you complete the stated conditions.
                                        </p>

                                        <p className="text-justify">
                                            This programme is a 12-month Top-up degree designed for the working professional. The quality of Kingston University (KU) is assured by QAA UK and is a member organization of the Association of Commonwealth Universities and internationally recognized. Also, LMU is a recognized university by the University Grants Commission of Sri Lanka.
                                        </p>

                                        <p className="text-justify">
                                            The partnership between ESOFT Metro Campus and London Metropolitan University was established in 2012 with the launching of the Top-up degree programme. Many successful undergraduates have paved a name for themselves as corporate leaders and entrepreneurs in the industry after completion of their graduation, you too will be an alumnus of a reputed and recognized global institution.
                                        </p>

                                        <p className="text-justify">
                                            We have commenced the registration of new students for the {previewData.intake}.
                                        </p>

                                        <p>
                                            I would like to warmly welcome you to ESOFT Metro Campus and the London Metropolitan University.
                                        </p>

                                        <p>Good luck.</p>
                                        <p>Thank you.</p>

                                        <p>
                                            Kindly put your name and signature to this as &ldquo;Received and Accepted the Offer&rdquo; to proceed.
                                        </p>
                                    </div>

                                    <div className="mt-10 grid grid-cols-2 gap-8 text-slate-700">
                                        <div className="space-y-2">
                                            <p className="font-semibold">Student Name:</p>
                                            <div className="h-6 rounded border-b border-dashed border-slate-400" />
                                        </div>
                                        <div className="space-y-2">
                                            <p className="font-semibold">Student Signature:</p>
                                            <div className="h-6 rounded border-b border-dashed border-slate-400" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </SalesLayout>
    );
}
